import React, { useRef, useEffect, useCallback } from 'react';
import { Range } from '@root/types';
import Chart from 'chart.js';
import styled from 'styled-components';
import BarChartConfiguration from './barChartConfiguration';
import getBarColors from './BarChartColors';

export interface IProps {
  data: number[];
  labels: Range[];
  title?: string;
  userRange: Range;
  yLabel?: string;
  xLabel?: string;
}

const BarChart = ({
  data,
  labels,
  title,
  yLabel,
  xLabel,
  userRange,
}: IProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const chartRef = useRef<Chart>();

  const getChartInstance = useCallback((): Chart | null => {
    if (chartRef.current) {
      return chartRef.current;
    }
    if (!canvasRef.current) {
      return null;
    }

    chartRef.current = new Chart(
      canvasRef.current,
      BarChartConfiguration({
        data,
        labels,
        title,
        yLabel,
        xLabel,
      }),
    );

    Chart.plugins.register({
      beforeDraw(chartInstance) {
        const { ctx } = chartInstance;

        if (ctx == null) {
          return;
        }

        ctx.fillStyle = 'white';
        ctx.fillRect(0, 0, chartInstance.width ?? 0, chartInstance.height ?? 0);
      },
    });

    return chartRef.current;
  }, [data, labels, title, xLabel, yLabel]);

  useEffect(() => {
    const chart = getChartInstance();

    if (
      chart != null &&
      chart.data.datasets &&
      chart.options.scales?.yAxes &&
      chart.options.scales?.xAxes
    ) {
      chart.data.datasets[0].data = data;
      chart.data.datasets[0].backgroundColor = getBarColors(userRange, labels);
      chart.data.labels = labels;

      chart.update();
    }
  }, [data, labels, title, yLabel, xLabel, userRange, getChartInstance]);

  return (
    <BarChartContainer>
      <div className="BarChart-header">
        <span className="BarChart-title">{title}</span>
      </div>

      {data.length === 0 ? (
        <div className="BarChart-centerOverlay">
          No data found for current filters.
        </div>
      ) : null}

      <canvas ref={canvasRef} style={{ width: '511px', height: '245px' }} />
    </BarChartContainer>
  );
};

const BarChartContainer = styled.div`
  padding: 10px;
  margin-top: 40px;
`;

export default BarChart;
